import {
    Component,
    OnInit,
    signal,
    computed,
    effect,
    input,
    output,
    ChangeDetectorRef,
    linkedSignal,
    ChangeDetectionStrategy,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25EditableModule } from "../../s25-editable/s25.editable.module";
import { S25TimepickerModule } from "../../s25-timepicker/s25.timepicker.module";
import {
    ObjectOccurrence,
    ObjectOccurrenceData,
    ResourceOccurrence,
    effectiveActRsrcCount,
    normalizeObjectOccurrenceData,
} from "../data.ingestion.service";
import {
    checkOccurrenceResourceAvailability,
    cloneOccurrencesWithResourceRowsCopied,
} from "./object.occurrence.availability";
import { NameService } from "../../../services/name.service";
import { S25ObjectOccurrenceLocationSearchComponent } from "./s25.object.occurrence.location.search.component";
import { S25ObjectOccurrenceResourceSearchComponent } from "./s25.object.occurrence.resource.search.component";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25Util } from "../../../util/s25-util";
import { Item } from "../../../pojo/Item";
import { DatetimeService } from "../../../services/datetime.service";
import { AlertService } from "../../../services/alert.service";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import {
    alignEndDayToStart,
    alignStartDayToEnd,
    applySameDayCheckFix,
    isSameCalendarDay,
} from "./object.occurrence.same.day";

@TypeManagerDecorator("s25-ng-object-occurrence-field")
@Component({
    selector: "s25-ng-object-occurrence-field",
    template: `
        <div class="object-occurrence-field">
            <!-- New Occurrence Input -->
            @if (!readOnly()) {
                <div class="new-occurrence-section">
                    <h4>Add New Occurrence</h4>
                    <div class="datetime-inputs">
                        <div class="datetime-row">
                            <div class="datetime-group">
                                <label>Start Date:</label>
                                <s25-ng-editable-date
                                    [val]="newStartDt()"
                                    (valChange)="onStartDateChange($event)"
                                    [alwaysEditing]="true"
                                ></s25-ng-editable-date>
                            </div>
                            <div class="datetime-group">
                                <label>Start Time:</label>
                                <s25-timepicker
                                    step="30"
                                    [modelValue]="newStartDt()"
                                    (modelValueChange)="newStartDt.set($event)"
                                    [inputId]="'newOccStartTime'"
                                ></s25-timepicker>
                            </div>
                        </div>
                        <div class="datetime-row">
                            @if (!onSameDay()) {
                                <div class="datetime-group">
                                    <label>End Date:</label>
                                    <s25-ng-editable-date
                                        [val]="newEndDt()"
                                        (valChange)="onEndDateChange($event)"
                                        [alwaysEditing]="true"
                                    ></s25-ng-editable-date>
                                </div>
                            }
                            <div class="datetime-group">
                                <label>End Time:</label>
                                <s25-timepicker
                                    step="30"
                                    [modelValue]="newEndDt()"
                                    (modelValueChange)="newEndDt.set($event)"
                                    [inputId]="'newOccEndTime'"
                                ></s25-timepicker>
                            </div>
                        </div>
                        <s25-ng-checkbox
                            class="same-day-checkbox"
                            [value]="onSameDay()"
                            (valueChange)="onSameDayToggle($event)"
                            labelClass="inline"
                        >
                            This begins and ends on the same day
                        </s25-ng-checkbox>
                        <s25-ng-button [type]="'primary'" [onClick]="addOccurrence" class="add-occurrence-btn">
                            Add Occurrence
                        </s25-ng-button>
                    </div>
                </div>
            }

            <!-- Existing Occurrences -->
            @if (occurrences().length > 0) {
                <div class="occurrences-list">
                    <h4>Occurrences</h4>
                    @for (occ of occurrences(); track occ.occurrenceUuid) {
                        <div class="occurrence-item">
                            <div class="occurrence-header">
                                <div class="occurrence-dates">
                                    <strong>{{ formatDatetime(occ.startDate) }}</strong> to
                                    <strong>{{ formatDatetime(occ.endDate) }}</strong>
                                </div>
                                @if (!readOnly()) {
                                    <s25-ng-button
                                        [type]="'danger--outline'"
                                        [onClick]="removeOccurrence.bind(this, occ.occurrenceUuid)"
                                        class="remove-btn"
                                        [ariaLabel]="'Remove occurrence'"
                                    >
                                        <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                    </s25-ng-button>
                                }
                            </div>

                            @if (showLocations()) {
                                @if (occ.locationIds.length > 0) {
                                    <div class="selected-locations">
                                        <strong>Selected Locations:</strong>
                                        <ul>
                                            @for (locId of occ.locationIds; track locId) {
                                                <li class="location-item">
                                                    <span>{{ getLocationName(locId) }}</span>
                                                    @if (!readOnly()) {
                                                        <s25-ng-button
                                                            [type]="'danger--outline'"
                                                            [onClick]="
                                                                removeLocation.bind(this, occ.occurrenceUuid, locId)
                                                            "
                                                            class="remove-location-btn"
                                                            [ariaLabel]="'Remove location'"
                                                        >
                                                            <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                                        </s25-ng-button>
                                                    }
                                                </li>
                                            }
                                        </ul>
                                    </div>
                                } @else if (!readOnly()) {
                                    <div class="no-locations">
                                        <em>No locations selected for this occurrence</em>
                                    </div>
                                }

                                @if (!readOnly()) {
                                    <s25-ng-object-occurrence-location-search
                                        [occurrenceUuid]="occ.occurrenceUuid"
                                        [occurrences]="occurrences()"
                                        [readOnly]="readOnly()"
                                        [totalOccurrences]="totalOccurrences()"
                                        [existingObjectIds]="occ.locationIds"
                                        (locationPicked)="onLocationPicked($event, occ.occurrenceUuid)"
                                    />
                                }
                            }

                            @if (showResources()) {
                                @if (occ.resources?.length) {
                                    <div class="selected-resources c-margin-top--single">
                                        <strong>Selected Resources:</strong>
                                        <ul>
                                            @for (resourceEntry of occ.resources; track resourceEntry.resourceId) {
                                                <li class="location-item">
                                                    <span class="resource-row-name">{{
                                                        getResourceName(resourceEntry.resourceId)
                                                    }}</span>
                                                    @if (!readOnly()) {
                                                        <label class="resource-qty-label">
                                                            <span class="resource-qty-label-text">Qty</span>
                                                            <input
                                                                type="number"
                                                                class="cn-form__control resource-qty-input"
                                                                min="1"
                                                                step="1"
                                                                [value]="effectiveActRsrcCount(resourceEntry)"
                                                                (change)="
                                                                    updateResourceActRsrcCount(
                                                                        occ.occurrenceUuid,
                                                                        resourceEntry.resourceId,
                                                                        $any($event.target).value
                                                                    )
                                                                "
                                                            />
                                                        </label>
                                                        <s25-ng-button
                                                            [type]="'danger--outline'"
                                                            [onClick]="
                                                                removeResource.bind(
                                                                    this,
                                                                    occ.occurrenceUuid,
                                                                    resourceEntry.resourceId
                                                                )
                                                            "
                                                            class="remove-location-btn"
                                                            [ariaLabel]="'Remove resource'"
                                                        >
                                                            <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                                        </s25-ng-button>
                                                    } @else {
                                                        <span class="resource-qty-display"
                                                            >Qty {{ effectiveActRsrcCount(resourceEntry) }}</span
                                                        >
                                                    }
                                                </li>
                                            }
                                        </ul>
                                    </div>
                                } @else if (!readOnly()) {
                                    <div class="no-locations c-margin-top--single">
                                        <em>No resources selected for this occurrence</em>
                                    </div>
                                }

                                @if (!readOnly()) {
                                    <s25-ng-object-occurrence-resource-search
                                        [occurrenceUuid]="occ.occurrenceUuid"
                                        [occurrences]="occurrences()"
                                        [totalOccurrences]="occurrences().length"
                                        [existingObjectIds]="resourceIdsForOccurrence(occ)"
                                        (resourcePicked)="onResourcePicked($event, occ.occurrenceUuid)"
                                    />
                                }
                            }

                            <div class="occurrence-comment">
                                <label><strong>Notes:</strong></label>
                                @if (!readOnly()) {
                                    <textarea
                                        class="cn-form__control"
                                        [value]="occ.comment || ''"
                                        (input)="updateComment(occ.occurrenceUuid, $any($event.target).value)"
                                        placeholder="Enter free-form info (e.g., 'Meet at the cafeteria entrance')"
                                        rows="2"
                                    ></textarea>
                                } @else if (occ.comment) {
                                    <p>{{ occ.comment }}</p>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        .object-occurrence-field {
            padding: 1rem;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background-color: var(--color--surface-2-bg);
        }

        .new-occurrence-section {
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid var(--color--border);
        }

        .datetime-inputs {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .datetime-row {
            display: flex;
            gap: 0.5rem;
            align-items: flex-end;
        }

        .datetime-group {
            flex: 0 0 auto;
        }

        .datetime-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .occurrence-item {
            border: 1px solid var(--color--border);
            border-radius: 4px;
            padding: 1rem;
            margin-bottom: 1rem;
            background-color: var(--color--surface-bg);
        }

        .occurrence-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
        }

        .occurrence-dates {
            flex: 1;
        }

        .location-selection-global {
            margin: 1rem 0;
            padding: 1rem;
            background-color: color-mix(in srgb, var(--cn-blue) 15%, var(--color--surface-bg));
            border-radius: 4px;
            border: 1px solid color-mix(in srgb, var(--cn-blue) 35%, var(--color--border));
        }

        .search-controls {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
        }

        .search-method-selector {
            padding-bottom: 1rem;
            border-bottom: 1px solid var(--color--border);
        }

        .search-method-label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .search-method-options {
            display: flex;
            gap: 1.5rem;
        }

        .search-method-option {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
        }

        .search-method-option input[type="radio"] {
            margin: 0;
            cursor: pointer;
        }

        .search-dropdown-section,
        .search-text-section {
            width: 100%;
        }

        .clear-search-btn {
            margin-top: 0.5rem;
        }

        .location-selection-global label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
        }

        .text-search-input-group {
            display: flex;
            gap: 0.5rem;
        }

        .text-search-input {
            flex: 1;
            padding: 0.5rem;
        }

        .search-btn {
            white-space: nowrap;
        }

        .search-loading {
            padding: 0.5rem;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .location-results {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        .location-results h5 {
            margin: 0 0 0.5rem 0;
            font-size: 1rem;
        }

        .location-results-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }

        .location-result-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem;
            margin-bottom: 0.5rem;
            background-color: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
        }

        .location-info {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex: 1;
        }

        .location-name {
            font-weight: 500;
        }

        .availability-badge {
            color: var(--color--text-muted);
            font-size: 0.9rem;
        }

        .add-location-btn {
            white-space: nowrap;
        }

        .no-results {
            padding: 1rem;
            text-align: center;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .no-locations {
            margin-top: 1rem;
            padding: 0.5rem;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .occurrence-comment {
            margin-top: 0.75rem;
        }

        .occurrence-comment label {
            display: block;
            margin-bottom: 0.25rem;
        }

        .occurrence-comment textarea {
            width: 100%;
        }

        .selected-locations {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }

        .selected-locations strong {
            display: block;
            margin-bottom: 0.5rem;
        }

        .selected-locations ul {
            list-style: none;
            padding: 0;
            margin: 0;
        }

        .location-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem;
            background-color: var(--color--surface-2-bg);
            border-radius: 4px;
            margin-bottom: 0.5rem;
        }

        .location-item span {
            flex: 1;
        }

        .resource-row-name {
            flex: 1;
            min-width: 0;
        }

        .resource-qty-label {
            display: flex;
            align-items: center;
            gap: 0.35rem;
            margin: 0 0.5rem;
            white-space: nowrap;
        }

        .resource-qty-label-text {
            font-size: 0.85rem;
            font-weight: 500;
        }

        .resource-qty-input {
            width: 4rem;
            min-width: 3rem;
        }

        .resource-qty-display {
            margin: 0 0.5rem;
            color: var(--color--text-muted);
            font-size: 0.9rem;
            white-space: nowrap;
        }

        .add-occurrence-btn {
            align-self: flex-start;
        }

        .remove-btn,
        .remove-location-btn {
            height: fit-content;
            padding: 0.25rem 0.5rem;
        }

        h4 {
            margin: 0 0 1rem 0;
            color: var(--color--text);
        }

        ::ng-deep .ngTimepicker .ngDropdownPaginated {
            margin-top: -35px;

            .select2-choice {
                font-size: 0.7rem;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        S25ButtonComponent,
        S25IconComponent,
        S25EditableModule,
        S25TimepickerModule,
        S25ObjectOccurrenceLocationSearchComponent,
        S25ObjectOccurrenceResourceSearchComponent,
        S25CheckboxModule,
    ],
})
export class S25ObjectOccurrenceFieldComponent implements OnInit {
    readonly effectiveActRsrcCount = effectiveActRsrcCount;

    value = input<ObjectOccurrenceData | undefined>(undefined); // ObjectOccurrenceData object
    readOnly = input<boolean>(false);
    /** From field_def.field_config; default true when unset. */
    showLocations = input<boolean>(true);
    showResources = input<boolean>(true);
    valueChange = output<ObjectOccurrenceData | undefined>(); // Emits ObjectOccurrenceData object

    newStartDt = signal<Date>(new Date());
    newEndDt = signal<Date>(new Date());
    readonly onSameDay = linkedSignal({
        source: () => ({ start: this.newStartDt(), end: this.newEndDt() }),
        computation: ({ start, end }) => isSameCalendarDay(start, end),
    });

    occurrences = signal<ObjectOccurrence[]>([]);
    locationNames = signal<{ [locationId: number]: string }>({});
    resourceNames = signal<{ [resourceId: number]: string }>({});
    dateTimeFormat = signal<string | undefined>(undefined);

    totalOccurrences = computed(() => this.occurrences().length);

    constructor(private cd: ChangeDetectorRef) {
        // Watch for changes to occurrences and emit when they change
        effect(() => {
            this.emitChange(this.occurrences());
        });
    }

    async ngOnInit() {
        this.loadValue(this.value());

        const [dateTimeFormat, currentHour, nextHour] = await Promise.all([
            UserprefService.getS25DateTimeformat(),
            DatetimeService.currentHour(),
            DatetimeService.nextHour(),
        ]);
        this.dateTimeFormat.set(dateTimeFormat);
        this.newStartDt.set(currentHour);
        this.newEndDt.set(nextHour);
    }

    loadValue(data: ObjectOccurrenceData | undefined) {
        if (!data || !data.occurrences) {
            this.occurrences.set([]);
            return;
        }
        const normalized = normalizeObjectOccurrenceData({ occurrences: data.occurrences });
        this.occurrences.set(normalized?.occurrences ?? []);
        void this.loadLocationNames();
        void this.loadResourceNames();
    }

    async loadLocationNames() {
        const allLocationIds = this.occurrences()
            .flatMap((occ) => occ.locationIds)
            // remove duplicates
            .filter((id, index, arr) => arr.indexOf(id) === index);

        if (allLocationIds.length === 0) {
            return;
        }

        // Only fetch names for location IDs that we don't already have
        const currentNames = this.locationNames();
        const missingIds = allLocationIds.filter((id) => !currentNames[id]);

        if (missingIds.length === 0) {
            return; // All names already loaded
        }

        const names = await NameService.getNames(
            missingIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Location })),
        );

        // Merge new names with existing names
        const updatedNames = { ...currentNames };
        missingIds.forEach((id) => {
            updatedNames[id] = names[`${id}-${Item.Ids.Location}`] || "Unknown Location";
        });
        this.locationNames.set(updatedNames);
    }

    getLocationName(locationId: number): string {
        return this.locationNames()[locationId] || "Loading...";
    }

    async loadResourceNames() {
        const allResourceIds = this.occurrences()
            .flatMap((occ) => occ.resources?.map((resourceEntry) => resourceEntry.resourceId) ?? [])
            // unique ids
            .filter((resourceId, index, arr) => arr.indexOf(resourceId) === index);

        if (allResourceIds.length === 0) {
            return;
        }

        const currentNames = this.resourceNames();
        const missingIds = allResourceIds.filter((resourceId) => !currentNames[resourceId]);
        if (missingIds.length === 0) {
            return;
        }

        const names = await NameService.getNames(
            missingIds.map((resourceId) => ({ itemId: resourceId, itemTypeId: Item.Ids.Resource })),
        );

        const updatedNames = { ...currentNames };
        missingIds.forEach((resourceId) => {
            updatedNames[resourceId] = names[`${resourceId}-${Item.Ids.Resource}`] || "Unknown Resource";
        });
        this.resourceNames.set(updatedNames);
    }

    getResourceName(resourceId: number): string {
        return this.resourceNames()[resourceId] || "Loading...";
    }

    formatDatetime(isoString: string): string {
        const date = new Date(isoString);
        const format = this.dateTimeFormat();
        if (format) {
            return S25Datefilter.transform(isoString, format) as string;
        }
        return date.toLocaleString();
    }

    addOccurrence = async () => {
        const startDate = this.newStartDt();
        const endDate = this.newEndDt();

        if (!startDate || !endDate) {
            AlertService.alert("Please select both start and end dates");
            return;
        }

        if (endDate <= startDate) {
            AlertService.alert("End date must be after start date");
            return;
        }

        const newOcc: ObjectOccurrence = {
            occurrenceUuid: `occ_${Date.now()}`,
            startDate: S25Util.date.toS25ISODateTimeStrFromDate(startDate),
            endDate: S25Util.date.toS25ISODateTimeStrFromDate(endDate),
            locationIds: [],
            resources: [],
            comment: "",
        };

        this.occurrences.update((occs) => [...occs, newOcc]);

        const [currentHour, nextHour] = await Promise.all([DatetimeService.currentHour(), DatetimeService.nextHour()]);
        this.newStartDt.set(currentHour);
        this.newEndDt.set(nextHour);
    };

    onSameDayToggle = (checked: boolean) => {
        this.onSameDay.set(checked);
        if (!checked) {
            return;
        }
        this.newEndDt.set(applySameDayCheckFix(this.newStartDt(), this.newEndDt()));
    };

    onStartDateChange = (newDate: Date) => {
        const keepSameDay = this.onSameDay();
        const updatedStart = S25Util.date.clone(this.newStartDt());
        S25Util.date.syncDateToDate(updatedStart, newDate);
        this.newStartDt.set(updatedStart);
        if (keepSameDay) {
            this.newEndDt.set(alignEndDayToStart(updatedStart, this.newEndDt()));
        }
    };

    onEndDateChange = (newDate: Date) => {
        const keepSameDay = this.onSameDay();
        const updatedEnd = S25Util.date.clone(this.newEndDt());
        S25Util.date.syncDateToDate(updatedEnd, newDate);
        this.newEndDt.set(updatedEnd);
        if (keepSameDay) {
            this.newStartDt.set(alignStartDayToEnd(this.newStartDt(), updatedEnd));
        }
    };

    removeOccurrence = (occurrenceUuid: string) => {
        this.occurrences.update((occs) => occs.filter((occ) => occ.occurrenceUuid !== occurrenceUuid));
    };

    onLocationPicked = (event: { locationId: number; locationName: string }, occurrenceUuid: string) => {
        const occurrence = this.occurrences().find((occ) => occ.occurrenceUuid === occurrenceUuid);
        if (!occurrence || occurrence.locationIds.includes(event.locationId)) {
            return;
        }
        occurrence.locationIds.push(event.locationId);
        this.occurrences.update((occs) => [...occs]);
        this.locationNames.update((names) => ({
            ...names,
            [event.locationId]: event.locationName,
        }));
        this.cd.detectChanges();
    };

    resourceIdsForOccurrence(occurrence: ObjectOccurrence): number[] {
        return (occurrence.resources ?? []).map((resourceEntry) => resourceEntry.resourceId);
    }

    onResourcePicked = (
        event: { resourceId: number; resourceName: string; actRsrcCount: number },
        occurrenceUuid: string,
    ) => {
        const occurrence = this.occurrences().find((occ) => occ.occurrenceUuid === occurrenceUuid);
        if (!occurrence) {
            return;
        }
        if (!occurrence.resources) {
            occurrence.resources = [];
        }
        const pickedCount = Number.isFinite(event.actRsrcCount) && event.actRsrcCount > 0 ? event.actRsrcCount : 1;
        const existing = occurrence.resources.find((resourceEntry) => resourceEntry.resourceId === event.resourceId);
        if (existing) {
            return;
        }
        occurrence.resources.push({ resourceId: event.resourceId, actRsrcCount: pickedCount });
        this.occurrences.update((occs) => [...occs]);
        this.resourceNames.update((names) => ({
            ...names,
            [event.resourceId]: event.resourceName,
        }));
        this.cd.detectChanges();
    };

    updateResourceActRsrcCount = async (
        occurrenceUuid: string,
        resourceId: number,
        rawValue: string,
    ): Promise<void> => {
        const baseOccurrences = this.occurrences();
        const occurrence = baseOccurrences.find((occ) => occ.occurrenceUuid === occurrenceUuid);
        const resourceEntry = occurrence?.resources?.find((entry) => entry.resourceId === resourceId);
        if (!resourceEntry || !occurrence) {
            return;
        }

        const newQuantity = S25Util.clamp(S25Util.parseInt(rawValue), 1, Infinity);
        const previousEffective = effectiveActRsrcCount(resourceEntry);
        if (newQuantity === previousEffective) {
            return;
        }

        const previousRaw = resourceEntry.actRsrcCount;

        const testOccurrences = cloneOccurrencesWithResourceRowsCopied(baseOccurrences);
        const testOccurrence = testOccurrences.find((occ) => occ.occurrenceUuid === occurrenceUuid);
        const testResourceEntry = testOccurrence?.resources?.find((entry) => entry.resourceId === resourceId);
        if (!testOccurrence || !testResourceEntry) {
            return;
        }
        testResourceEntry.actRsrcCount = newQuantity;

        try {
            const availabilityByOccurrence = await checkOccurrenceResourceAvailability(testOccurrences);
            if (!availabilityByOccurrence.get(occurrenceUuid)?.has(resourceId)) {
                AlertService.alert(
                    "That quantity is not available for this occurrence and time. The quantity was reset to the previous value.",
                );
                this.restoreResourceActRsrcCount(resourceEntry, previousRaw);
                this.occurrences.update((occList) => [...occList]);
                this.cd.markForCheck();
                return;
            }
        } catch (caughtError) {
            console.error("Resource availability check failed", caughtError);
            AlertService.alert("Could not verify availability. The quantity was reset to the previous value.");
            this.restoreResourceActRsrcCount(resourceEntry, previousRaw);
            this.occurrences.update((occList) => [...occList]);
            this.cd.markForCheck();
            return;
        }

        resourceEntry.actRsrcCount = newQuantity;
        this.occurrences.update((occList) => [...occList]);
        this.cd.markForCheck();
    };

    private restoreResourceActRsrcCount(resourceEntry: ResourceOccurrence, previousRaw: number | undefined): void {
        if (previousRaw != null && previousRaw > 0) {
            resourceEntry.actRsrcCount = previousRaw;
        } else {
            delete resourceEntry.actRsrcCount;
        }
    }

    removeLocation = (occurrenceUuid: string, locationId: number) => {
        const occurrence = this.occurrences().find((occ) => occ.occurrenceUuid === occurrenceUuid);
        if (occurrence) {
            occurrence.locationIds = occurrence.locationIds.filter((id) => id !== locationId);
            this.occurrences.update((occs) => [...occs]);
        }
    };

    updateComment(occurrenceUuid: string, value: string) {
        const occurrence = this.occurrences().find((occ) => occ.occurrenceUuid === occurrenceUuid);
        if (occurrence) {
            occurrence.comment = value;
            this.occurrences.update((occs) => [...occs]);
        }
    }

    removeResource = (occurrenceUuid: string, resourceId: number) => {
        const occurrence = this.occurrences().find((occ) => occ.occurrenceUuid === occurrenceUuid);
        if (occurrence?.resources?.length) {
            occurrence.resources = occurrence.resources.filter(
                (resourceEntry) => resourceEntry.resourceId !== resourceId,
            );
            this.occurrences.update((occs) => [...occs]);
        }
    };

    emitChange(occurrences: ObjectOccurrence[]) {
        this.valueChange.emit({ occurrences: occurrences });
    }
}
